import { ipc } from '../../services/ElectronIPC'
import { AudioEngineError, AudioEngineStreamStatsEvent } from './index'
import { AudioEngineRemoteAudio } from '../../RoomContext'
import FakeMediaStreamTrack from './FakeMediaStreamTrack'
import { IpcRendererEvent } from 'electron'
import { AudioInputStreamInfo, AudioTrackWithInfo } from '../../UserContext'
import { env } from '../../services/EnvironmentVariables'
import { AudioEngineJoinRoomParams, IAudioEngineRoom } from './IAudioEngineRoom'
import { AudioPatch } from './types'

export class AudioEngineRoom implements IAudioEngineRoom {
    remoteStreams: Map<string, AudioEngineRemoteAudio> = new Map()

    constructor() {
        this.onStreamStats = this.onStreamStats.bind(this)
        ipc.on('AudioEngine.streamStatsEvent', this.onStreamStats)
    }

    private onStreamStats(_: IpcRendererEvent, ev: AudioEngineStreamStatsEvent) {
        Object.keys(ev.remoteStreamStats).forEach((streamUuid) => {
            const stats = ev.remoteStreamStats[streamUuid] || null
            const stream = this.remoteStreams.get(streamUuid) || null
            const track = stream?.track as unknown as FakeMediaStreamTrack
            if (stats && track instanceof FakeMediaStreamTrack) {
                // console.log(`*** (streamUuid='${streamUuid}') internalSetCurrentRemoteStats() stats=` + JSON.stringify(stats))
                track.internalSetCurrentRemoteStats(stats)
            }
        })
    }

    async joinRoom(data: AudioEngineJoinRoomParams): Promise<string | AudioEngineError> {
        return new Promise((resolve, reject) => {
            ipc.send('AudioEngine.joinRoom', {
                roomUrn: data.roomUrn,
                serverUrls: data.servers.map((s) => s.url),
                userInfo: data.userInfo,
            })
            ipc.once(
                'AudioEngine.joinRoom',
                (_ev: Electron.IpcRendererEvent, response: { result: string } | { error: AudioEngineError }) => {
                    if ('error' in response) {
                        reject(response.error)
                        return
                    }

                    resolve('native-relay-unknown')
                }
            )
        })
    }

    async exitRoom(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            ipc.send('AudioEngine.joinRoom', { roomUrn: '' })
            ipc.once(
                'AudioEngine.joinRoom',
                (_ev: Electron.IpcRendererEvent, response: { result: string } | { error: AudioEngineError }) => {
                    if ('error' in response) {
                        reject(response.error)
                        return
                    }

                    resolve(true)
                }
            )
        })
    }

    async sendLocalStream(track: AudioTrackWithInfo): Promise<boolean> {
        return new Promise((resolve, reject) => {
            ipc.send('AudioEngine.sendLocalStream', {
                streamUuid: track.id,
            })
            ipc.once(
                'AudioEngine.sendLocalStream',
                (_ev: Electron.IpcRendererEvent, response: { result: string } | { error: AudioEngineError }) => {
                    // clearTimeout(this.timeoutTimers.get('getAudioDevices'))
                    if ('error' in response) {
                        reject(response.error)
                        return
                    }

                    resolve(true)
                }
            )
        })
    }

    async receiveRemoteStream(
        streamUuid: string,
        info: AudioInputStreamInfo
    ): Promise<AudioEngineRemoteAudio | AudioEngineError> {
        return new Promise((resolve) => {
            // backward compatibility
            if (info.receiverBufferMaxDriftMs == null) {
                info.receiverBufferMaxDriftMs = 0
            }
            if (info.networkPacketDurationMs == null) {
                info.networkPacketDurationMs = 0
            }

            if (env.TEST_DELAY_AT_RECEIVE_REMOTE_STREAM) {
                // experienced this, but could not replicate: without this, when the remote peer
                // changes the audio quality, the audio stream will not be played back although the
                // network usage indicates that the audio stream is being received.
                setTimeout(() => {
                    ipc.send('AudioEngine.receiveRemoteStream', {
                        streamUuid,
                        info: info,
                    })
                }, 250)
            } else {
                ipc.send('AudioEngine.receiveRemoteStream', {
                    streamUuid,
                    info: info,
                })
            }

            ipc.once(
                'AudioEngine.receiveRemoteStream',
                (_ev: Electron.IpcRendererEvent, response: { result: string } | { error: AudioEngineError }) => {
                    // clearTimeout(this.timeoutTimers.get('getAudioDevices'))
                    if ('error' in response) {
                        resolve(response.error)
                        return
                    }

                    let audioEngineStreamUuid = info.audioEngineStreamUuid || ''
                    if (!audioEngineStreamUuid) {
                        resolve(
                            new AudioEngineError(
                                'receiveRemoteStream',
                                'expected audioEngineStreamUuid to exist, but got null!'
                            )
                        )
                        return
                    }

                    const stream: AudioEngineRemoteAudio = {
                        fromTopology: 'ae',
                        track: new FakeMediaStreamTrack({
                            id: audioEngineStreamUuid,
                            label: info.label,
                            channelSelection: info.channelSelection,
                            channelUnpackingMap: info.channelUnpackingMap,
                            streamSettingsChangedHandler: ({ isMuted, volume }) => {
                                const data: any = { streamUuid: streamUuid }
                                if (isMuted != null) {
                                    data.isMuted = isMuted
                                }
                                if (volume != null) {
                                    data.volume = volume
                                }

                                ipc.send('AudioEngine.setAudioStreamSettings', data)
                            },
                        }),
                        info: info,
                    }

                    this.remoteStreams.set(streamUuid, stream)

                    resolve(stream)
                }
            )
        })
    }

    async removeRemoteStream(streamUuid: string): Promise<boolean> {
        return new Promise((resolve, reject) => {
            ipc.send('AudioEngine.removeRemoteStream', {
                streamUuid,
            })

            ipc.once(
                'AudioEngine.removeRemoteStream',
                (_ev: Electron.IpcRendererEvent, response: { result: string } | { error: AudioEngineError }) => {
                    if ('error' in response) {
                        reject(response.error)
                        return
                    }

                    resolve(true)
                }
            )
        })
    }

    createAudioPatch(sourceStreamId: string, targetDeviceId: string, targetChannel: number): void {
        throw new Error('Method not implemented.')
    }

    removeAudioPatch(sourceStreamId: string): void {
        throw new Error('Method not implemented.')
    }

    removeAllAudioPatches(): void {
        throw new Error('Method not implemented.')
    }

    listAudioPatches(): AudioPatch[] {
        throw new Error('Method not implemented.')
    }

    destroy() {
        this.exitRoom()
        ipc.removeListener('AudioEngine.streamStatsEvent', this.onStreamStats)
    }
}
